import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface SearchMultiSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  items: { id: string; name: string }[];
  selectedItems: string[];
  onSelect: (items: string[]) => void;
  searchPlaceholder?: string;
  multiSelect?: boolean;
}

// Модалка множественного выбора с поиском
const SearchMultiSelectModal: React.FC<SearchMultiSelectModalProps> = ({
  isOpen,
  onClose,
  title,
  items,
  selectedItems,
  onSelect,
  searchPlaceholder = 'Поиск...',
  multiSelect = true
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  const filteredItems = items.filter((item) =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleItemClick = (id: string) => {
    if (multiSelect) {
      if (selectedItems.includes(id)) {
        onSelect(selectedItems.filter((i) => i !== id));
      } else {
        onSelect([...selectedItems, id]);
      }
    } else {
      onSelect([id]);
      onClose();
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <>
      <div className="fixed inset-0 bg-black/30 z-[10001]" onClick={onClose} />
      <div className="fixed inset-0 z-[10002] flex items-center justify-center p-4">
        <div className="bg-white rounded-[24px] w-full max-w-md max-h-[80vh] flex flex-col shadow-xl">
          <div className="flex items-center justify-between p-6 border-b border-[#E2E4E9]">
            <h3 className="text-xl font-medium text-[#0A0D14]">{title}</h3>
            <button type="button" onClick={onClose} className="p-1 hover:bg-[#F6F8FA] rounded-lg">
              <XMarkIcon className="w-6 h-6 text-[#525866]" />
            </button>
          </div>
          <div className="p-4 border-b border-[#E2E4E9]">
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2.5 rounded-[10px] border border-[#E2E4E9] bg-white text-sm"
            />
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {filteredItems.map((item) => {
              const isSelected = selectedItems.includes(item.id);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleItemClick(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-[10px] text-left mb-1 transition-colors ${
                    isSelected ? 'bg-[#FEF3F2] text-[#E16349]' : 'hover:bg-[#F6F8FA] text-[#525866]'
                  }`}
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'border-[#E16349] bg-[#E16349]' : 'border-[#CDD0D5]'}`}>
                    {isSelected && (
                      <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                        <path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <span className="text-sm">{item.name}</span>
                </button>
              );
            })}
          </div>
          <div className="p-4 border-t border-[#E2E4E9]">
            <button
              type="button"
              onClick={onClose}
              className="w-full py-3 bg-[#E16349] text-white rounded-[12px] font-medium text-lg hover:bg-[#C9573F] transition-colors"
            >
              Применить
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default SearchMultiSelectModal;
